// =====================================================================
// Toast state — pure, so the awkward part is testable without React.
//
// The awkward part is DISMISSAL. Most toast libraries model a toast as an
// event ("something happened, show a message, fade it out"). The cockpit's
// toasts are mostly CONDITIONS ("you are on the wrong microphone"), which are
// true continuously until something changes. Two things follow, and both are
// bugs if you get them wrong:
//
//   - A condition re-announces itself constantly. The mic snapshot re-reports
//     on every deviceChange, so a naive push() would stack a dozen identical
//     toasts. Toasts are therefore keyed by a caller-chosen `id`: showing the
//     same id again REPLACES in place rather than appending.
//
//   - If dismissal doesn't stick, the toast is worse than no toast. Dismiss it,
//     plug a headset in somewhere else, and the condition re-fires — the thing
//     you just closed comes straight back. A dismissed id stays suppressed
//     until `clear` says the condition actually resolved, at which point the
//     next occurrence is allowed to speak again.
//
// So: `show` is safe to call every render, `dismiss` is the user's veto for
// this episode, and `clear` ends the episode.
// =====================================================================

export type ToastTone = 'info' | 'warn';

export interface ToastAction {
  label: string;
  onClick: () => void;
}

export interface Toast {
  // Stable per condition, NOT per occurrence — this is what makes a repeat
  // show() an update instead of a new row.
  id: string;
  tone: ToastTone;
  title: string;
  body?: string;
  // A toast that states a problem should offer the fix; without one the SDR has
  // to go find the control themselves, which is what the icon nudge already asks.
  action?: ToastAction;
  dismissible: boolean;
}

export interface ToastState {
  // Insertion-ordered; a replaced toast keeps its original position so the
  // stack doesn't reshuffle under the pointer mid-click.
  toasts: Toast[];
  // Ids the user vetoed. Suppresses show() until the matching clear().
  dismissed: string[];
}

export const emptyToastState: ToastState = { toasts: [], dismissed: [] };

export type ToastEvent =
  | { type: 'show'; toast: Toast }
  | { type: 'dismiss'; id: string } // the user closed it
  | { type: 'clear'; id: string }; // the condition resolved

export function reduceToasts(state: ToastState, event: ToastEvent): ToastState {
  switch (event.type) {
    case 'show': {
      const { toast } = event;
      if (state.dismissed.includes(toast.id)) return state; // vetoed this episode
      const at = state.toasts.findIndex((t) => t.id === toast.id);
      if (at === -1) return { ...state, toasts: [...state.toasts, toast] };
      // Replace in place. Bail if nothing actually changed so a show() on every
      // render doesn't produce a new array and re-render the viewport forever.
      if (sameToast(state.toasts[at]!, toast)) return state;
      const toasts = [...state.toasts];
      toasts[at] = toast;
      return { ...state, toasts };
    }
    case 'dismiss': {
      if (state.dismissed.includes(event.id)) return state;
      return {
        toasts: state.toasts.filter((t) => t.id !== event.id),
        dismissed: [...state.dismissed, event.id],
      };
    }
    case 'clear': {
      const present = state.toasts.some((t) => t.id === event.id);
      const vetoed = state.dismissed.includes(event.id);
      if (!present && !vetoed) return state;
      return {
        toasts: state.toasts.filter((t) => t.id !== event.id),
        // Forget the veto: the condition is gone, so a LATER occurrence is a new
        // episode and gets to speak.
        dismissed: state.dismissed.filter((d) => d !== event.id),
      };
    }
  }
}

// Field-wise, ignoring `action` identity: callers build the action inline, so a
// fresh closure every render would otherwise look like a change forever.
function sameToast(a: Toast, b: Toast): boolean {
  return (
    a.tone === b.tone &&
    a.title === b.title &&
    a.body === b.body &&
    a.dismissible === b.dismissible &&
    a.action?.label === b.action?.label
  );
}
